import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, MenuItem, LinearProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { CompareArrows, TrendingUp, TrendingDown } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const fmtHKD = (n: number) => `HK$${Number(n).toLocaleString('en-HK', { minimumFractionDigits: 2 })}`;
const diffColor = (d: number) => d > 0 ? '#2e7d32' : d < 0 ? '#c62828' : 'text.secondary';

interface Run { id: string; periodName: string; periodStartDate: string; totalNetPay: number; status: string; }
interface Variance { employeeId: string; name: string; dept: string; totalA: number; totalB: number; diff: number; pct: number | null; isNew: boolean; isRemoved: boolean; }
interface Report { runA: Run; runB: Run; summaryA: any; summaryB: any; diffGross: number; diffNet: number; variances: Variance[]; unchanged: number; }

export default function PayrollVariance() {
  const [runs, setRuns] = useState<Run[]>([]);
  const [runA, setRunA] = useState('');
  const [runB, setRunB] = useState('');
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterFlag, setFilterFlag] = useState('all');

  useEffect(() => {
    apiFetch('/api/payroll-variance/runs').then(r => {
      setRuns(r || []);
      if (r?.length >= 2) { setRunA(r[1].id); setRunB(r[0].id); }
    }).catch((e: any) => setError(e.message));
  }, []);

  const compare = useCallback(async () => {
    if (!runA || !runB || runA === runB) return;
    setLoading(true); setError('');
    try {
      const r = await apiFetch(`/api/payroll-variance?runA=${runA}&runB=${runB}`);
      setReport(r);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [runA, runB]);

  const displayed = report?.variances.filter(v => {
    if (filterFlag === 'new') return v.isNew;
    if (filterFlag === 'removed') return v.isRemoved;
    if (filterFlag === 'increase') return !v.isNew && !v.isRemoved && v.diff > 0;
    if (filterFlag === 'decrease') return !v.isNew && !v.isRemoved && v.diff < 0;
    return true;
  }) ?? [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CompareArrows sx={{ fontSize: 32, color: '#1a237e' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Payroll Variance Report</Typography>
          <Typography variant="body2" color="text.secondary">Compare two payroll runs and identify differences</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Run picker */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField label="Base Run (A)" select size="small" sx={{ minWidth: 220 }} value={runA} onChange={e => setRunA(e.target.value)}>
            {runs.map(r => <MenuItem key={r.id} value={r.id}>{r.periodName} ({r.status})</MenuItem>)}
          </TextField>
          <CompareArrows color="action" />
          <TextField label="Compare Run (B)" select size="small" sx={{ minWidth: 220 }} value={runB} onChange={e => setRunB(e.target.value)}>
            {runs.map(r => <MenuItem key={r.id} value={r.id}>{r.periodName} ({r.status})</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={compare} disabled={!runA || !runB || runA === runB}>Compare</Button>
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {report && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Gross Pay Δ', value: report.diffGross, isMoney: true },
              { label: 'Net Pay Δ',   value: report.diffNet,   isMoney: true },
              { label: 'Changed',     value: report.variances.length, isMoney: false },
              { label: 'Unchanged',   value: report.unchanged,        isMoney: false },
            ].map(s => (
              <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
                <Card sx={{ textAlign: 'center', p: 1, border: s.isMoney && s.value !== 0 ? `2px solid ${s.value > 0 ? '#4caf50' : '#f44336'}` : undefined }}>
                  <CardContent sx={{ pb: '8px !important' }}>
                    <Typography variant="h5" fontWeight={700} color={s.isMoney ? diffColor(s.value) : 'text.primary'}>
                      {s.isMoney ? (s.value >= 0 ? '+' : '') + fmtHKD(s.value) : s.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Run totals comparison */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Run Summary</Typography>
            <Grid container spacing={2}>
              {(['gross', 'deductions', 'net'] as const).map(key => (
                <Grid key={key} size={{ xs: 12, sm: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{key}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2">{fmtHKD(report.summaryA[key])}</Typography>
                    <CompareArrows sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="body2" fontWeight={700}>{fmtHKD(report.summaryB[key])}</Typography>
                    {(() => {
                      const d = report.summaryB[key] - report.summaryA[key];
                      return <Chip label={(d >= 0 ? '+' : '') + fmtHKD(d)} size="small" sx={{ bgcolor: d > 0 ? '#e8f5e9' : d < 0 ? '#ffebee' : '#f5f5f5', color: diffColor(d), fontWeight: 700 }} />;
                    })()}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Filter chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {[
              { v: 'all',      l: `All Changes (${report.variances.length})` },
              { v: 'new',      l: `New Employees (${report.variances.filter(v => v.isNew).length})` },
              { v: 'removed',  l: `Removed (${report.variances.filter(v => v.isRemoved).length})` },
              { v: 'increase', l: `Increases (${report.variances.filter(v => !v.isNew && !v.isRemoved && v.diff > 0).length})` },
              { v: 'decrease', l: `Decreases (${report.variances.filter(v => !v.isNew && !v.isRemoved && v.diff < 0).length})` },
            ].map(f => (
              <Chip key={f.v} label={f.l} size="small" onClick={() => setFilterFlag(f.v)}
                color={filterFlag === f.v ? 'primary' : 'default'} variant={filterFlag === f.v ? 'filled' : 'outlined'} />
            ))}
          </Box>

          {/* Variance table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Department</b></TableCell>
                  <TableCell align="right"><b>Run A (Base)</b></TableCell>
                  <TableCell align="right"><b>Run B (Compare)</b></TableCell>
                  <TableCell align="right"><b>Difference</b></TableCell>
                  <TableCell align="right"><b>Change %</b></TableCell>
                  <TableCell><b>Flag</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map(v => (
                  <TableRow key={v.employeeId} hover sx={{ bgcolor: v.isNew ? '#f1f8e9' : v.isRemoved ? '#fce4ec' : undefined }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{v.name}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{v.dept}</Typography></TableCell>
                    <TableCell align="right">{fmtHKD(v.totalA)}</TableCell>
                    <TableCell align="right">{fmtHKD(v.totalB)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        {v.diff > 0 ? <TrendingUp sx={{ fontSize: 14, color: '#2e7d32' }} /> : <TrendingDown sx={{ fontSize: 14, color: '#c62828' }} />}
                        <Typography fontWeight={700} color={diffColor(v.diff)}>
                          {v.diff >= 0 ? '+' : ''}{fmtHKD(v.diff)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {v.pct !== null ? (
                        <Typography variant="body2" color={diffColor(v.diff)}>
                          {v.pct >= 0 ? '+' : ''}{v.pct}%
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {v.isNew && <Chip label="🆕 New" size="small" color="success" />}
                      {v.isRemoved && <Chip label="🗑 Removed" size="small" color="error" />}
                      {!v.isNew && !v.isRemoved && Math.abs(v.diff) > 5000 && <Chip label="⚠️ Large" size="small" color="warning" />}
                    </TableCell>
                  </TableRow>
                ))}
                {displayed.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No variances match the filter.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!report && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <CompareArrows sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography sx={{ mt: 1 }}>Select two payroll runs above and click Compare</Typography>
        </Paper>
      )}
    </Box>
  );
}
